import invariant from 'invariant'
import { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import i18n from '@signavio/i18n'
import { Box } from '@signavio/ui/core'
import { Divider, PrimaryButton, DangerHint } from '@signavio/ui'

import { Field } from '@signavio/effektif-fields'
import {
  ChoiceFieldT,
  ElementInstance,
  Field as FieldT,
  FieldInstance,
  FieldSingleValue,
  TListValue,
  TSingleValue,
} from '@signavio/effektif-api'
import { Nullable } from '@signavio/effektif-commons'

import {
  checkDynamicFieldChange,
  findElement,
  replaceElement,
  validateFields,
} from '../utils'
import FormTree from './FormTree'

type ValidationT = {
  isFormValid: boolean
}

type FormPropsT = {
  initialFields?: ElementInstance[]
  fields: ElementInstance[]
  readOnly?: boolean
  hideSubmitAction?: boolean
  submitText?: string
  onSubmit?: (decision?: Nullable<ChoiceFieldT>) => void
  onChange?: (field: FieldT, validation?: ValidationT) => void
  newFieldsDesign?: boolean
}

function Form({
  initialFields,
  fields = [],
  readOnly,
  hideSubmitAction,
  submitText = i18n('Done'),
  onSubmit,
  onChange,
  newFieldsDesign,
}: FormPropsT) {
  const [errors, setErrors] = useState(
    validateFields(fields, {
      skipRequiredCheck: true,
    })
  )

  const [submitIntent, setSubmitIntent] = useState(false)

  useDynamicFieldsUpdate(fields, initialFields, onChange, setErrors)

  const handleSubmit = useCallback(
    (decision?: string, id?: string) => {
      // do not validate the decision because it has not been set
      // but will be set onSubmit after the form itself is valid
      const errors = validateFields(
        fields.filter(
          (element) =>
            element.elementType === 'fieldGroupInstance' || !element.asButtons
        )
      )

      setSubmitIntent(true)
      if (hasErrors(errors)) {
        setErrors(errors)

        return
      }

      if (!onSubmit) {
        return
      }

      if (decision && id) {
        const [, decisionField] = updateFields(fields, decision, id)

        onSubmit(decisionField)
      } else {
        onSubmit()
      }
    },
    [fields, onSubmit]
  )

  const buttonsField = useMemo(
    () =>
      fields.find(
        (field) => field.elementType === 'fieldInstance' && field.asButtons
      ) as FieldSingleValue | undefined,

    [fields]
  )

  const elements = useMemo(() => {
    return fields.filter((element) => element !== buttonsField)
  }, [fields, buttonsField])

  invariant(
    !buttonsField || buttonsField.elementType === 'fieldInstance',
    'The field for buttons was not a "fieldInstance".'
  )

  return (
    <>
      {hasErrors(errors) && (
        <Box marginBottom="small">
          <DangerHint>
            {i18n(
              'The entered form data is invalid. Please check the fields highlighted in red.'
            )}
          </DangerHint>
        </Box>
      )}

      <FormTree
        newFieldsDesign={newFieldsDesign}
        errors={errors}
        // @ts-ignore SIGPG-4908
        elements={elements}
        onComplete={(value: TSingleValue | TListValue, id: string) => {
          const [updatedFields, updatedField] = updateFields(fields, value, id)

          const newErrors = validateFields(updatedFields, {
            skipRequiredCheck: !submitIntent,
          })

          setErrors(newErrors)

          const updatedFieldIsValid =
            id in newErrors && newErrors[id].length === 0

          if (
            onChange &&
            (updatedFieldIsValid || updatedField.value === null)
          ) {
            onChange(updatedField)
          }
        }}
        readOnly={readOnly}
      />

      {!hideSubmitAction && (
        <>
          <Divider />

          {buttonsField ? (
            <Field
              asButtons
              clearable={false}
              value={buttonsField.value}
              type={buttonsField.type}
              onComplete={(value: string) => {
                if (hasErrors(errors)) {
                  return
                }

                handleSubmit(value, buttonsField.id)
              }}
              readOnly={readOnly || buttonsField.readOnly}
            />
          ) : (
            <PrimaryButton
              block
              onClick={() => {
                if (hasErrors(errors)) {
                  return
                }
                handleSubmit()
              }}
              disabled={readOnly}
            >
              {submitText}
            </PrimaryButton>
          )}
        </>
      )}
    </>
  )
}

const useDynamicFieldsUpdate = (
  fields: ElementInstance[],
  initialFields: ElementInstance[] | undefined,
  onChange: ((field: FieldT, validation?: ValidationT) => void) | undefined,
  setErrors: (errors: Record<string, string[]>) => void
) => {
  const fieldsRef = useRef(fields)
  useEffect(() => {
    if (!onChange || !initialFields) {
      return
    }

    const changedFields: FieldInstance[] = checkDynamicFieldChange(
      initialFields,
      fieldsRef.current,
      fields
    )

    if (changedFields.length > 0) {
      const newFields = changedFields.reduce((newFields, changedField) => {
        const [updatedFields] = updateFields(
          newFields,
          changedField.value,
          changedField.id
        )

        return updatedFields
      }, fields)

      const errors = validateFields(newFields, {
        skipRequiredCheck: true,
      })

      setErrors(errors)

      fieldsRef.current = newFields

      const isFormValid = !hasErrors(errors)

      changedFields.forEach((changedField) => {
        onChange(changedField, {
          isFormValid,
        })
      })
    } else {
      fieldsRef.current = fields
    }
  }, [fields, initialFields, onChange, setErrors])
}

function updateFields(
  elements: ElementInstance[],
  value: Nullable<TSingleValue | TListValue | undefined>,
  id: string
) {
  const field = findElement(elements, { id })
  invariant(field, `Could not find field instance to update with id "${id}".`)

  invariant(
    field.elementType === 'fieldInstance',
    `Element with id ${id} is not a field, but ${field.elementType}`
  )

  const updatedField = {
    ...field,
    value,
  }

  return [replaceElement(elements, updatedField), updatedField]
}

const hasErrors = (errors: Record<string, string[]>) =>
  Object.values(errors).some((fieldErrors) => {
    invariant(Array.isArray(fieldErrors), 'Field errors must be an array')

    return fieldErrors.length > 0
  })

export default Form
